import { action, observable, computed } from 'mobx';
import { WebGivingPage } from '../index';
import { validateFields, ValidationState } from '../../validation/view-model-validator';
import {
	WebGivingMerchant, CardBrandConfig, PaymentMethodType, MerchantCardPreference,
	WebGivingPersonModel, WebGivingAddPaymentMethodResultCode,
	SplitLineItem
} from '../../webgiving-generated';
import { getCardBrandsFromKeys } from '../../utils/card-utils';
import { CardPaymentMethodViewModel } from './card-payment-method-view-model';
import { ITabOption } from '../../components/tabs/tabs';
import { AchPaymentMethodViewModel } from './ach-payment-method-view-model';
import cardIcon from '../../assets/svg/icon-card.svg';
import achIcon from '../../assets/svg/checkbook.svg';
import { SvgProperties } from '../../components/svg-icon';
import { WebGivingPageViewModelBase } from '../page-view-model-base';
import { WebGivingErrorType, WebGivingError } from '../../components/error-message/error-message';
import { ApplePaymentMethodViewModel } from './apple-pay/apple-payment-method-view-model';

export type PaymentMethodApplePayParams = {
	amount: number;
	applePayAvailable: boolean;
	electedToCoverFees: boolean;
	showTermsAndConditions: boolean;
	oneTimeGiftDateLabel: string;
	paymentDetailsText: string;
	fundName: string;
	lineItems: SplitLineItem[];
}

export class PagePaymentMethodViewModel extends WebGivingPageViewModelBase {
	readonly type: WebGivingPage.PaymentMethod = WebGivingPage.PaymentMethod;

	// mobx observable decorator removed as it caused issues when running our front end test suites
	// it has been replaced with an observable syntax which achieves similar behavior
	paymentMethods: ITabOption[] = observable([]);

	@computed
	get paymentMethodViewModel(): AchPaymentMethodViewModel | CardPaymentMethodViewModel {
		return this.paymentMethodType === PaymentMethodType.CreditCard ? this.cardModel : this.achModel;
	}

	@computed
	get merchantSupportsCard(): boolean {
		return this.merchant.SupportedCardBrandKeys && this.merchant.SupportedCardBrandKeys.length > 0;
	}

	@observable
	paymentMethodType: PaymentMethodType;

	@observable
	validationState?: ValidationState;

	@observable
	cardModel: CardPaymentMethodViewModel;

	supportedCardBrands: CardBrandConfig[];

	@observable
	achModel: AchPaymentMethodViewModel;

	@observable
	applePayModel: ApplePaymentMethodViewModel | null = null;

	showBackToConfirmGift: boolean;

	initialPaymentMethodType?: PaymentMethodType;

	termsAndConditionsUrl?: string;

	privacyStatementUrl?: string;

	private errorsByTab: { [key: number]: WebGivingError };

	@action
	setError(error: WebGivingError): void {

		if (error && error.errorType === WebGivingErrorType.AddPaymentMethodError && error.errorResultCode === WebGivingAddPaymentMethodResultCode.CardTokenizationUnavailable) {
			this.createPaymentMethodTabs({ cardTokenizationUnavailable: true });
		}

		this.error = error;
	}

	@action
	validate(): ValidationState {
		return validateFields(this.paymentMethodViewModel, 'Please enter valid details');
	}

	constructor(
		public merchant: WebGivingMerchant,
		public person: WebGivingPersonModel,
		public ApplePayParams: PaymentMethodApplePayParams | null = null,
		public hideACH = false,
		paymentMethodType?: PaymentMethodType,
		achModel?: AchPaymentMethodViewModel,
		termsAndConditionsUrl?: string,
		privacyStatementUrl?: string
	) {
		super();
		this.achModel = achModel || new AchPaymentMethodViewModel();
		this.configureApplePayTabModel();
		this.cardModel = new CardPaymentMethodViewModel(merchant, person.PostalCodeCommonName, person.PostalCodeRegex);
		this.supportedCardBrands = getCardBrandsFromKeys(this.merchant.SupportedCardBrandKeys);
		this.errorsByTab = {};
		this.createPaymentMethodTabs();
		this.showBackToConfirmGift = person.PaymentMethods && person.PaymentMethods.length > 0;
		this.termsAndConditionsUrl = termsAndConditionsUrl;
		this.privacyStatementUrl = privacyStatementUrl;

		this.findSelectedPaymentMethod(paymentMethodType);
		this.bringSelectedPaymentMethodTabOnFront();
	}

	@action.bound
	switchPaymentMethodType(value: number) {
		if (this.paymentMethodType !== value) {
			this.validationState = undefined;
			this.paymentMethodViewModel.clearValidation();
		}

		if (this.error) {
			this.errorsByTab[this.paymentMethodType] = this.error;
		}
		this.error = this.errorsByTab[value];

		this.paymentMethodType = value;
	}

	configureApplePayTabModel() {
		if (this.ApplePayParams && this.ApplePayParams.applePayAvailable && this.merchantSupportsCard) {
			this.applePayModel = new ApplePaymentMethodViewModel(
				this.merchant,
				this.ApplePayParams
			);
		}
	}

	createPaymentMethodTabs(options?: { cardTokenizationUnavailable: boolean }) {
		this.paymentMethods = [];
		this.addCreditCardPaymentMethod(options);
		this.addAchPaymentMethod();
		this.addApplePayPaymentMethod();
	}

	findSelectedPaymentMethod(paymentMethodType?: PaymentMethodType) {
		// separated the .logic of selection as other payment types are coming, will be easier then (Google etc.)
		const applePayHasPriority = this.applePayModel && (!this.person.PaymentMethods || this.person.PaymentMethods.length == 0)
			&& this.merchant.PaymentMethodPreference == PaymentMethodType.CreditCard;

		if (applePayHasPriority) {
			this.paymentMethodType = PaymentMethodType.ApplePay;
		} else {
			this.paymentMethodType = paymentMethodType || this.merchant.PaymentMethodPreference;
			if (this.paymentMethodType === PaymentMethodType.ACH && this.hideACH) {
				const nonACHMethod = this.paymentMethods.filter(c => c.value !== PaymentMethodType.ACH)[0];
				if (nonACHMethod) this.paymentMethodType = nonACHMethod.value;
			}
		}

		// ensure there's a payment method selected
		if (!this.paymentMethodType) {
			this.paymentMethodType = this.paymentMethods[0].value;
		}
	}

	bringSelectedPaymentMethodTabOnFront() {
		// brings in front whatewer payment method is selected leaving others in their order
		if (this.paymentMethods && this.paymentMethods.length > 0) {
			if (this.paymentMethods[0].value === this.paymentMethodType) {
				return; // already in front position
			}

			const index = this.paymentMethods.findIndex(c => c.value === this.paymentMethodType);
			if (index > -1) {
				const [tab] = this.paymentMethods.splice(index, 1);
				this.paymentMethods.unshift(tab);
			}
		}
	}

	addCreditCardPaymentMethod(options?: { cardTokenizationUnavailable: boolean }) {
		const cardTabAvailabilityLabel = options && options.cardTokenizationUnavailable
			? ' (Not available)'
			: '';

		const cardTabLabel = this.merchant.CardPreference === MerchantCardPreference.AnyCard
			? 'Card' + cardTabAvailabilityLabel
			: 'Debit card' + cardTabAvailabilityLabel;

		if (this.merchantSupportsCard) {
			this.addPaymentMethod(cardTabLabel, PaymentMethodType.CreditCard, 'tab-payment-method-card', cardIcon);
		}
	}

	addAchPaymentMethod() {
		if (this.merchant.SupportsAch) {
			this.addPaymentMethod('Bank account', PaymentMethodType.ACH, 'tab-payment-method-ach', achIcon);
		}
	}

	addApplePayPaymentMethod() {
		if (this.applePayModel) {
			this.addPaymentMethod('Apple Pay®', PaymentMethodType.ApplePay, 'tab-payment-method-apple-pay', undefined, true);
		}
	}

	addPaymentMethod(label: string, value: PaymentMethodType, targetId: string, icon?: SvgProperties, disableTranslation?: boolean) {
		this.paymentMethods.push({
			label: label,
			value: value,
			icon: icon,
			acceptanceTestTargetId: targetId,
			disableTranslation: disableTranslation,
		});

	}

	@action.bound
	setApplePayParams(params: PaymentMethodApplePayParams | null) {
		if (params) {
			this.ApplePayParams = params;
			this.configureApplePayTabModel();
			this.createPaymentMethodTabs();
			this.paymentMethodType = PaymentMethodType.ApplePay;
			this.bringSelectedPaymentMethodTabOnFront();
		}
	}
}
